import { SYSTEM_INSTRUCTION_TEMPLATE, CHAT_SYSTEM_INSTRUCTION_TEMPLATE, DEFAULT_PLAYER_NAME } from '@/constants';
import { StorySegment, ChatMessage, Archetype, UserProfile, CompanionMessage, StatChanges } from '../types';
import { databaseService } from './databaseService';
import { ENV } from '@/config/env';

// Types for OpenRouter responses
interface OpenRouterStoryResponse {
  narrative: string;
  companion_dialogue: CompanionMessage[];
  choices: string[];
  image_prompt: string;
  stat_changes?: StatChanges;
}

interface OpenRouterChatResponseFormat {
  dialogues: CompanionMessage[];
  stat_changes?: StatChanges;
}
// Token tracking temporarily disabled until Supabase migration complete


// Token tracking service disabled until Supabase migration complete

// OpenRouter configuration
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

// Dynamic API key getter to handle Vercel deployment issues
const getOpenRouterApiKey = (): string | undefined => {
  // Use ENV wrapper for Capacitor compatibility
  const apiKey = ENV.OPENROUTER_API_KEY;
  
  // Debug logging for production troubleshooting
  if (import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true') {
    console.log('[OpenRouter] Environment check:', {
      hasApiKey: !!apiKey,
      keyLength: apiKey ? apiKey.length : 0,
      keyPrefix: apiKey ? apiKey.substring(0, 10) + '...' : 'undefined',
      environment: import.meta.env.VITE_ENVIRONMENT || 'unknown',
      allEnvKeys: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
    });
  }
  
  return apiKey;
};

const OPENROUTER_DEFAULT_MODEL = ENV.OPENROUTER_DEFAULT_MODEL || 'google/gemma-2-9b-it:free';

// Array of backup models to try in order - all verified working models
const OPENROUTER_BACKUP_MODELS = [
  ENV.OPENROUTER_BACKUP_MODEL || 'meta-llama/llama-3.2-3b-instruct:free',
  ENV.OPENROUTER_BACKUP2_MODEL || 'mistralai/mistral-7b-instruct:free',
  ENV.OPENROUTER_BACKUP3_MODEL || 'qwen/qwen-2.5-72b-instruct:free',
  ENV.OPENROUTER_BACKUP4_MODEL || 'deepseek/deepseek-chat-v3-0324:free'
];

const OPENROUTER_TEXT_MODEL = OPENROUTER_DEFAULT_MODEL;
const OPENROUTER_CHAT_MODEL = OPENROUTER_DEFAULT_MODEL;

// Check if we have a valid API key
const hasValidApiKey = (): boolean => {
  const apiKey = getOpenRouterApiKey();
  return apiKey !== null && 
         apiKey !== undefined &&
         typeof apiKey === 'string' &&
         apiKey.length > 10;
};

interface OpenRouterMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface OpenRouterResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// Circuit breaker state for moderation requests
let moderationCircuitOpen = false;
let moderationCircuitOpenUntil = 0;
const MODERATION_CIRCUIT_TIMEOUT = 30000; // 30 seconds

// OpenRouter request function with retry logic and backup model fallback
async function makeOpenRouterRequest(
  messages: OpenRouterMessage[],
  model: string = OPENROUTER_TEXT_MODEL,
  userId?: string,
  retryCount: number = 0,
  backupModelIndex: number = -1,
  options: { timeout?: number; isModeration?: boolean } = {}
): Promise<OpenRouterResponse> {
  
  // Check circuit breaker for moderation requests
  if (options.isModeration && moderationCircuitOpen) {
    if (Date.now() < moderationCircuitOpenUntil) {
      console.log('[OpenRouter] Circuit breaker OPEN for moderation - using fallback');
      throw new Error('Moderation circuit breaker is open');
    } else {
      // Reset circuit breaker
      moderationCircuitOpen = false;
      console.log('[OpenRouter] Circuit breaker CLOSED - retrying moderation');
    }
  }
  
  // Check if we have a valid API key
  if (!hasValidApiKey()) {
    const apiKey = getOpenRouterApiKey();
    const debugInfo = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true' ? 
      ` Debug: apiKey=${apiKey ? 'exists' : 'missing'}, length=${apiKey?.length || 0}, env=${import.meta.env.VITE_ENVIRONMENT}` : '';
    throw new Error(`[OpenRouter] No API key configured. Please set VITE_OPENROUTER_API_KEY in your environment variables.${debugInfo}`);
  }
  
  // Log API usage for tracking
  const startTime = Date.now();
  const maxRetries = 3;
  const retryDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s, 4s
  
  // Determine which model to use
  let currentModel: string;
  if (backupModelIndex >= 0 && backupModelIndex < OPENROUTER_BACKUP_MODELS.length) {
    currentModel = OPENROUTER_BACKUP_MODELS[backupModelIndex];
  } else {
    currentModel = model;
  }
  
  try {
    const apiKey = getOpenRouterApiKey();
    
    // Set timeout based on request type
    const timeout = options.timeout || (options.isModeration ? 5000 : 30000); // 5s for moderation, 30s for others
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://lucid-cyber-rpg.app',
        'X-Title': 'Lucid Cyber RPG'
      },
      body: JSON.stringify({
        model: currentModel,
        messages: messages,
        temperature: 0.8,
        max_tokens: options.isModeration ? 100 : 4000, // Smaller response for moderation
        top_p: 0.9,
        frequency_penalty: 0.1,
        presence_penalty: 0.1
      }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[OpenRouter] API error (${response.status}): ${errorText}`);
      
      // If it's an auth error, throw error to ensure dynamic content generation
      if (response.status === 401 || response.status === 403) {
        throw new Error('[OpenRouter] Authentication failed. Please contact support.');
      }
      
      // Handle 503 Service Unavailable specifically
      if (response.status === 503) {
        if (backupModelIndex < OPENROUTER_BACKUP_MODELS.length - 1) {
          // Try next backup model
          const nextBackupIndex = backupModelIndex + 1;
          console.log(`[OpenRouter] Model ${currentModel} failed with 503, trying backup model ${nextBackupIndex + 1}/${OPENROUTER_BACKUP_MODELS.length}: ${OPENROUTER_BACKUP_MODELS[nextBackupIndex]}`);
          return makeOpenRouterRequest(messages, model, userId, 0, nextBackupIndex);
        } else if (retryCount < maxRetries) {
          console.log(`[OpenRouter] 503 Service Unavailable - Retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return makeOpenRouterRequest(messages, model, userId, retryCount + 1, backupModelIndex);
        }
      }
      
      throw new Error(`OpenRouter API error (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    // CRITICAL DEBUG: Check if we're getting the "..." response issue
    let content = data.choices?.[0]?.message?.content;
    if (content === '...' || content === '....' || content === '.....') {
      console.error('🚨 [OpenRouter] CRITICAL: Received "..." response!');
      console.error('[OpenRouter] Full response data:', JSON.stringify(data, null, 2));
      console.error('[OpenRouter] Request model:', currentModel);
      console.error('[OpenRouter] Request messages:', JSON.stringify(messages, null, 2));
      console.error('[OpenRouter] Response time:', Date.now() - startTime, 'ms');
      throw new Error('[OpenRouter] AI model returned ellipsis response - this indicates a critical issue');
    }

    // Additional debug for short responses that might be truncated
    if (content && content.length < 50) {
      console.warn('⚠️ [OpenRouter] Received unusually short response:', content);
      console.warn('[OpenRouter] Response length:', content.length);
      console.warn('[OpenRouter] Full response:', JSON.stringify(data, null, 2));
    }

    // PRE-FLIGHT JSON VALIDATION
    if (content && !options.isModeration) {
      const trimmedContent = content.trim();

      // Check if response is properly formatted JSON
      const startsWithBrace = trimmedContent.startsWith('{');
      const endsWithBrace = trimmedContent.endsWith('}');

      if (!startsWithBrace || !endsWithBrace) {
        console.warn('[OpenRouter] ⚠️ Response not properly formatted as JSON');
        console.warn('[OpenRouter] First 100 chars:', trimmedContent.substring(0, 100));
        console.warn('[OpenRouter] Last 100 chars:', trimmedContent.substring(Math.max(0, trimmedContent.length - 100)));

        // Retry with enhanced prompt if this is first attempt
        if (retryCount < 1 && backupModelIndex === -1) {
          console.log('[OpenRouter] Retrying with JSON-enforcing prompt...');

          const enhancedMessages = [
            {
              role: 'system' as const,
              content: '⚠️ CRITICAL: You MUST respond with ONLY valid JSON. Start with { and end with }. NO markdown code blocks. NO explanations. ONLY JSON.',
            },
            ...messages,
          ];

          return makeOpenRouterRequest(
            enhancedMessages,
            model,
            userId,
            retryCount + 1,
            backupModelIndex,
            options
          );
        }
      }

      // Quick JSON validity check
      try {
        JSON.parse(trimmedContent);
        console.log('[OpenRouter] ✅ Pre-flight JSON validation passed');
      } catch (parseError) {
        console.warn('[OpenRouter] ⚠️ Pre-flight JSON validation failed:', parseError);
        // Don't throw yet - let the caller handle parsing with cleaning
      }
    }

    // Log successful API usage
    const responseTime = Date.now() - startTime;
    const tokensUsed = data.usage?.total_tokens || 0;

    if (userId) {
      await databaseService.logApiUsage('openrouter', currentModel, tokensUsed, 0);
    }

    console.log(`[OpenRouter] Request successful - Model: ${currentModel}, Tokens: ${tokensUsed}, Time: ${responseTime}ms`);

    return data;

  } catch (error) {
    const responseTime = Date.now() - startTime;
    console.error(`[OpenRouter] Request failed after ${responseTime}ms:`, error);
    
    // Handle timeout errors for moderation
    if (options.isModeration && error instanceof Error && error.name === 'AbortError') {
      console.log('[OpenRouter] Moderation request timed out - opening circuit breaker');
      moderationCircuitOpen = true;
      moderationCircuitOpenUntil = Date.now() + MODERATION_CIRCUIT_TIMEOUT;
      throw new Error('Moderation request timed out');
    }
    
    // Check if it's a 503 error and we haven't exceeded max retries
    if (error instanceof Error && error.message.includes('503')) {
        if (backupModelIndex < OPENROUTER_BACKUP_MODELS.length - 1) {
            // Try next backup model
            const nextBackupIndex = backupModelIndex + 1;
            console.log(`[OpenRouter] Model ${currentModel} failed with 503, trying backup model ${nextBackupIndex + 1}/${OPENROUTER_BACKUP_MODELS.length}: ${OPENROUTER_BACKUP_MODELS[nextBackupIndex]}`);
            return makeOpenRouterRequest(messages, model, userId, 0, nextBackupIndex, options);
        } else if (retryCount < maxRetries) {
            console.log(`[OpenRouter] 503 Service Unavailable - Retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return makeOpenRouterRequest(messages, model, userId, retryCount + 1, backupModelIndex, options);
        }
    }
    
    // Throw error to ensure dynamic content generation - no static fallbacks
    throw new Error(`[OpenRouter] Request failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

// REMOVED: Static fallback responses that override AI generation
// All content MUST be generated dynamically by OpenRouter AI model based on karma calculations
// Fallbacks only used for critical API connection failures, not content generation

// Generate story content using OpenRouter with karma-based dynamics
export async function generateStoryContent(
  prompt: string,
  storyHistory: StorySegment[],
  archetype: Archetype,
  userProfile: UserProfile | null,
  userId?: string,
  karmaContext?: { currentKarma: number; recentChoices: string[]; location: string }
): Promise<OpenRouterStoryResponse> {
  console.log('🎮 [OpenRouter] generateStoryContent called');
  console.log('[OpenRouter] Prompt:', prompt?.substring(0, 100) + '...');
  console.log('[OpenRouter] Archetype:', archetype?.name);
  console.log('[OpenRouter] User ID:', userId);
  console.log('[OpenRouter] Has API key:', hasValidApiKey());
  
  try {
    // Build karma-aware system prompt
    const karmaInfo = karmaContext ? `
    KARMA CONTEXT:
    - Current Karma: ${karmaContext.currentKarma}
    - Location: ${karmaContext.location}
    - Recent Choices: ${karmaContext.recentChoices.slice(-3).join(', ')}
    - Karma Level: ${karmaContext.currentKarma > 20 ? 'Virtuous' : karmaContext.currentKarma < -20 ? 'Corrupted' : 'Balanced'}
    ` : '';

    const systemPrompt = `You are an immersive cyberpunk RPG narrator creating rich, detailed story content for Neo-Veridia 2087.

    ⚠️ CRITICAL OUTPUT FORMAT REQUIREMENT ⚠️
    You MUST respond with ONLY valid JSON. No markdown code blocks. No explanations. No extra text.
    Your response must start with { and end with }
    DO NOT wrap your response in \`\`\`json or any other markdown formatting.

    NARRATIVE REQUIREMENTS:
    - Write 3-5 detailed paragraphs (200-400 words) per narrative
    - Include sensory details: neon lights, digital sounds, cyber-augmentation visuals
    - Describe the environment, atmosphere, and character interactions vividly
    - Build tension and intrigue with each scene
    - Reference the cyberpunk setting explicitly (neural implants, data streams, corporate districts)

    CHARACTER ARCHETYPE: ${archetype.name} - ${archetype.description}
    SETTING: Neo-Veridia 2087 - A sprawling megacity where the digital and physical realms blur
    ${karmaInfo}

    EXACT JSON FORMAT (copy this structure exactly):
    {
      "narrative": "A rich, immersive 200-400 word cyberpunk narrative that vividly describes the current scene, atmosphere, and player's experience in Neo-Veridia. Include specific cyberpunk elements like neural interfaces, holographic displays, data streams, and the digital-physical hybrid environment.",
      "companion_dialogue": [
        {"name": "JAX", "dialogue": "Tech-focused response with cyberpunk jargon and practical observations about the digital environment"},
        {"name": "Lyra", "dialogue": "Philosophical response about consciousness, digital existence, and the deeper meanings in the cyber-reality"}
      ],
      "choices": ["Detailed action choice 1 with cyberpunk context", "Detailed action choice 2 with technical elements", "Detailed action choice 3 with social/philosophical implications"],
      "image_prompt": "Detailed cyberpunk scene description for image generation"
    }

    CRITICAL JSON RULES:
    1. Start your response with { (opening brace)
    2. End your response with } (closing brace)
    3. NO markdown code blocks (\`\`\`json is FORBIDDEN)
    4. Properly escape all quotes inside strings with \"
    5. Narrative must be 200-400 words and immersive
    6. JAX references technical/hacking elements, Lyra explores consciousness themes
    7. Choices must be meaningful and contextually rich
    8. All content must feel authentically cyberpunk with detailed world-building`;

    const messages: OpenRouterMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: prompt }
    ];

    const response = await makeOpenRouterRequest(messages, OPENROUTER_TEXT_MODEL, userId);
    const content = response.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content received from OpenRouter');
    }
    
    console.log(`[OpenRouter] Final content preview: ${content?.substring(0, 50)}...`);
    
    // Check if the response is ONLY ellipsis (indicating an error)
    // Don't reject content that merely contains ellipsis as punctuation
    const trimmedContent = content.trim();
    if (trimmedContent === '...' || trimmedContent === '....' || trimmedContent === '.....' ||
        (trimmedContent.length < 10 && /^\.+$/.test(trimmedContent))) {
      console.error('[OpenRouter] Received invalid response (only ellipsis or too short):', content);
      throw new Error('[OpenRouter] AI model returned incomplete response - retrying with backup model');
    }

    // Parse the JSON response with robust error handling
    let parsed: any;
    try {
      // First attempt: direct parsing
      parsed = JSON.parse(content);
    } catch (parseError) {
      console.warn('[OpenRouter] Initial JSON parsing failed, attempting comprehensive cleaning...');
      console.log('[OpenRouter] Original content length:', content.length);
      console.log('[OpenRouter] First 500 chars:', content.substring(0, 500));
      console.log('[OpenRouter] Parse error:', parseError instanceof Error ? parseError.message : String(parseError));
      
      // Systematic content cleaning approach
      let cleanedContent = cleanContent(content);
      
      try {
        parsed = JSON.parse(cleanedContent);
        console.log('[OpenRouter] Successfully parsed cleaned JSON');
      } catch (secondParseError) {
        console.warn('[OpenRouter] Standard cleaning failed, attempting truncation repair...');
        console.log('[OpenRouter] Cleaned content length:', cleanedContent.length);
        console.log('[OpenRouter] Cleaned content preview:', cleanedContent.substring(0, 200) + '...');
        
        // Check if response was truncated and attempt repair
        const repairedContent = repairTruncatedJson(cleanedContent);
        
        try {
          parsed = JSON.parse(repairedContent);
          console.log('[OpenRouter] Successfully parsed repaired JSON');
        } catch (repairError) {
          console.error('[OpenRouter] All parsing attempts failed');
          console.error('[OpenRouter] Final content preview:', repairedContent.substring(0, 300) + '...');
          console.error('[OpenRouter] Parse error at position:', (repairError as any).message);
          
          // Last resort: attempt minimal reconstruction
          parsed = reconstructMinimalResponse(cleanedContent);
          console.log('[OpenRouter] Using minimal reconstructed response');
        }
      }
    }
    
    // Validate and provide fallbacks for missing fields in truncated responses
    // For autonomous agents, they return "action" instead of "narrative"
    if (!parsed.narrative && !parsed.action && !parsed.dialogue) {
      console.warn('[OpenRouter] Missing narrative/action/dialogue in response');
      throw new Error('[OpenRouter] Incomplete AI response - narrative, action, or dialogue is required');
    }

    // If this is an autonomous agent response (has action/dialogue but no narrative), that's valid
    if (parsed.action && parsed.dialogue && !parsed.narrative) {
      console.log('[OpenRouter] Autonomous agent response detected (action/dialogue format)');
      // Don't throw error, this is valid for agents
    }
    
    // Ensure narrative meets minimum quality standards (only if narrative exists)
    if (parsed.narrative && parsed.narrative.length < 50) {
      console.warn('[OpenRouter] Narrative too short, requesting regeneration');
      throw new Error('[OpenRouter] Generated narrative is too brief - requesting enhanced content');
    }
    
    // Always ensure companion dialogue exists
    if (!parsed.companion_dialogue || !Array.isArray(parsed.companion_dialogue) || parsed.companion_dialogue.length === 0) {
      console.warn('[OpenRouter] Missing or empty companion_dialogue, generating contextual fallback');

      // Determine best available text to ground fallback dialogue
      const fallbackText = typeof parsed.narrative === 'string' && parsed.narrative.length > 0
        ? parsed.narrative
        : typeof parsed.dialogue === 'string' && parsed.dialogue.length > 0
          ? parsed.dialogue
          : typeof parsed.action === 'string' && parsed.action.length > 0
            ? parsed.action
            : '';

      // Generate context-aware companion dialogue based on available narrative context
      const narrativeSnippet = fallbackText.substring(0, 100).toLowerCase();
      let jaxMessage = "Scanning the area... Data streams look active.";
      let lyraMessage = "I sense a shift in the digital currents here.";
      
      if (narrativeSnippet.includes("danger") || narrativeSnippet.includes("threat")) {
        jaxMessage = "Warning: Hostile signatures detected. Stay sharp.";
        lyraMessage = "The shadows here carry malevolent code. Be cautious.";
      } else if (narrativeSnippet.includes("safe") || narrativeSnippet.includes("peaceful")) {
        jaxMessage = "Area secure. All systems nominal.";
        lyraMessage = "The data flows peacefully here. A moment of digital zen.";
      }
      
      parsed.companion_dialogue = [
        { name: "JAX", dialogue: jaxMessage },
        { name: "Lyra", dialogue: lyraMessage }
      ];
    }
    
    if (!parsed.choices || !Array.isArray(parsed.choices) || parsed.choices.length === 0) {
      console.warn('[OpenRouter] Missing or invalid choices, using default options');
      parsed.choices = [
        { id: 1, text: "Investigate the area carefully" },
        { id: 2, text: "Move forward cautiously" },
        { id: 3, text: "Take a moment to assess the situation" }
      ];
    }
    
    if (!parsed.image_prompt) {
      console.warn('[OpenRouter] Missing image_prompt, generating from narrative');
      const fallbackText = typeof parsed.narrative === 'string' && parsed.narrative.length > 0
        ? parsed.narrative
        : typeof parsed.dialogue === 'string' && parsed.dialogue.length > 0
          ? parsed.dialogue
          : typeof parsed.action === 'string' && parsed.action.length > 0
            ? parsed.action
            : '';
      const narrativeSnippet = fallbackText.substring(0, 100);
      parsed.image_prompt = `Scene depicting: ${narrativeSnippet}... cyberpunk atmosphere, detailed environment`;
    }

    return {
      narrative: parsed.narrative,
      companion_dialogue: parsed.companion_dialogue,
      choices: parsed.choices,
      image_prompt: parsed.image_prompt,
      stat_changes: parsed.stat_changes
    };

  } catch (error) {
    console.error('[OpenRouter] Error generating story content:', error);
    
    // No static fallbacks - throw error to ensure dynamic content generation
    throw new Error(`[OpenRouter] Story content generation failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

// Generate chat responses using OpenRouter
// Comprehensive content cleaning function
function cleanContent(content: string): string {
  let cleaned = content.trim();

  // Step 0: Aggressively remove markdown code blocks FIRST (before anything else)
  // This handles the common case where AI wraps JSON in ```json ... ```
  cleaned = cleaned
    .replace(/^```json\s*/gim, '')      // Remove opening ```json
    .replace(/^```[a-z]*\s*/gim, '')    // Remove opening ```lang
    .replace(/\s*```$/gm, '')           // Remove closing ```
    .replace(/```json/gi, '')           // Remove inline ```json
    .replace(/```/g, '')                // Remove any remaining backticks
    .trim();

  // Step 1: Handle escaped characters first
  cleaned = cleaned
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');

  // Step 2: AGGRESSIVELY extract JSON from ANY wrapping
  // Find first { and last }, extract everything between
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');

  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    // Extract ONLY the JSON object, discarding everything else
    cleaned = cleaned.substring(firstBrace, lastBrace + 1);
    console.log('[OpenRouter] Extracted JSON from position', firstBrace, 'to', lastBrace);

    // Fix escaped newlines and quotes that break JSON parsing
    cleaned = cleaned
      .replace(/\\n/g, ' ')       // Replace \n with space
      .replace(/\\"/g, '"')       // Un-escape quotes
      .replace(/\s+/g, ' ')       // Collapse multiple spaces
      .trim();
  }
  
  // Step 3: Extract JSON object from surrounding text
  const jsonExtractionPatterns = [
    /(\{[\s\S]*\})/,                           // Standard JSON object
    /^[^{]*(\{[\s\S]*\})[^}]*$/,              // JSON with surrounding text
    /```[^`]*(\{[\s\S]*\})[^`]*```/,          // JSON in leftover code blocks
    /(?:Here'?s|This is)[^{]*(\{[\s\S]*\})/i  // Common response prefixes
  ];
  
  for (const pattern of jsonExtractionPatterns) {
    const match = cleaned.match(pattern);
    if (match && match[1]) {
      cleaned = match[1].trim();
      break;
    }
  }
  
  // Step 4: Handle truncation indicators (ellipsis)
  // Remove ellipsis that might break JSON
  cleaned = cleaned
    .replace(/\.\.\."\s*$/g, '"')     // Ellipsis at end of string
    .replace(/\.\.\.\s*\}/g, '}')     // Ellipsis before closing brace
    .replace(/\.\.\.\s*\]/g, ']')     // Ellipsis before closing bracket
    .replace(/\.\.\.\s*,/g, ',')      // Ellipsis before comma
    .replace(/,\s*\.\.\./g, '')       // Comma followed by ellipsis
    .replace(/\.\.\./g, '');           // Remove any remaining ellipsis
  
  // Step 5: Fix common JSON formatting issues
  cleaned = cleaned
    // Remove trailing commas
    .replace(/,\s*([\}\]])/g, '$1')
    // Remove double commas
    .replace(/,+/g, ',')
    // Fix smart quotes
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    // Fix double escapes
    .replace(/\\\\/g, "\\")
    // Remove control characters (except \t\n\r)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, "")
    // Fix unescaped newlines in strings
    .replace(/"([^"]*?)\n([^"]*?)"/g, '"$1\\n$2"')
    .replace(/"([^"]*?)\r([^"]*?)"/g, '"$1\\r$2"')
    .replace(/"([^"]*?)\t([^"]*?)"/g, '"$1\\t$2"')
    // Fix missing commas after strings
    .replace(/("\s*)\s+"/g, '$1,"')
    // Fix missing commas after numbers
    .replace(/([0-9])\s+"/g, '$1,"')
    // Quote unquoted property names
    .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
    // Quote unquoted string values (but not numbers/booleans/null)
    .replace(/:\s*([a-zA-Z_][a-zA-Z0-9_\s]+)(\s*[,}\]])/g, (match, value, suffix) => {
      const trimmedValue = value.trim();
      if (['true', 'false', 'null'].includes(trimmedValue.toLowerCase())) {
        return `: ${trimmedValue}${suffix}`;
      }
      return `: "${trimmedValue}"${suffix}`;
    });
  
  return cleaned;
}

// Function to repair truncated JSON
function repairTruncatedJson(content: string): string {
  // Check if JSON is actually truncated
  const openBraces = (content.match(/{/g) || []).length;
  const closeBraces = (content.match(/}/g) || []).length;
  const openBrackets = (content.match(/\[/g) || []).length;
  const closeBrackets = (content.match(/\]/g) || []).length;
  
  // If balanced, return as-is
  if (openBraces === closeBraces && openBrackets === closeBrackets && content.endsWith('}')) {
    return content;
  }
  
  console.log(`[OpenRouter] Detected truncation - Braces: ${openBraces}/${closeBraces}, Brackets: ${openBrackets}/${closeBrackets}`);
  
  let repaired = content;
  
  // Find likely truncation point by looking for incomplete structures
  const truncationIndicators = [
    repaired.lastIndexOf('"},'),
    repaired.lastIndexOf('"],'),
    repaired.lastIndexOf('"'),
    repaired.lastIndexOf('}'),
    repaired.lastIndexOf(']')
  ];
  
  const lastCompletePosition = Math.max(...truncationIndicators);
  
  // If we found a clean break point and it's near the end, truncate there
  if (lastCompletePosition > repaired.length * 0.7) {
    const cleanBreakChar = repaired[lastCompletePosition];
    if (cleanBreakChar === '}' || cleanBreakChar === ']' || cleanBreakChar === '"') {
      repaired = repaired.substring(0, lastCompletePosition + 1);
    }
  }
  
  // Handle incomplete strings (odd number of unescaped quotes)
  // More robust approach: check if last character suggests unclosed string
  const unescapedQuotes = (repaired.match(/(?<!\\)"/g) || []).length;
  if (unescapedQuotes % 2 !== 0) {
    console.log('[OpenRouter] Detected incomplete string - attempting to close');
    // Only add closing quote if the truncation looks like it's in a string value
    // Check if last quote is followed by a colon (key) or not (value)
    const lastQuoteIndex = repaired.lastIndexOf('"');
    const textAfterLastQuote = repaired.substring(lastQuoteIndex + 1).trim();
    // If text after last quote doesn't start with colon, it's likely a value string
    if (lastQuoteIndex >= 0 && !textAfterLastQuote.startsWith(':')) {
      repaired += '"';
    }
  }
  
  // Close incomplete arrays first (innermost structures)
  const bracketDiff = openBrackets - closeBrackets;
  for (let i = 0; i < bracketDiff; i++) {
    repaired += ']';
  }
  
  // Close incomplete objects
  const braceDiff = openBraces - closeBraces;
  for (let i = 0; i < braceDiff; i++) {
    repaired += '}';
  }
  
  // Clean up any trailing commas that might have been exposed
  repaired = repaired.replace(/,\s*([\}\]])/g, '$1');
  
  console.log(`[OpenRouter] Repaired JSON - added ${bracketDiff} brackets and ${braceDiff} braces`);
  return repaired;
}

// Function to reconstruct minimal valid response when all parsing fails
function reconstructMinimalResponse(content: string): any {
  console.log('[OpenRouter] Attempting minimal response reconstruction...');
  
  // Try to extract any narrative text we can find
  const narrativePatterns = [
    /"narrative"\s*:\s*"([^"]*)"/i,
    /"narrative"\s*:\s*'([^']*)'/i,
    /narrative[:\s]+([^,}\n]+)/i
  ];
  
  let narrative = "The digital realm flickers as connections stabilize...";
  for (const pattern of narrativePatterns) {
    const match = content.match(pattern);
    if (match && match[1] && match[1].length > 10) {
      narrative = match[1].trim();
      break;
    }
  }
  
  // Try to extract choices if present
  let choices = ["Continue forward", "Assess the situation", "Wait and observe"];
  const choicesMatch = content.match(/"choices"\s*:\s*\[([^\]]*)\]/i);
  if (choicesMatch) {
    try {
      const choicesText = choicesMatch[1];
      const extractedChoices = choicesText.split(',').map(c => 
        c.replace(/['"]/g, '').trim()
      ).filter(c => c.length > 0);
      
      if (extractedChoices.length > 0) {
        choices = extractedChoices.slice(0, 3); // Max 3 choices
      }
    } catch (e) {
      console.warn('[OpenRouter] Could not extract choices from truncated response');
    }
  }
  
  // Create minimal valid response structure
  const reconstructed = {
    narrative: narrative,
    companion_dialogue: [
      { name: "JAX", dialogue: "Systems are recalibrating. Give me a moment to process this." },
      { name: "Lyra", dialogue: "The dream state seems unstable here. Let's proceed with caution." }
    ],
    choices: choices,
    image_prompt: `cyberpunk scene depicting: ${narrative.substring(0, 50)}...`
  };
  
  console.log('[OpenRouter] Reconstructed minimal response with narrative:', narrative.substring(0, 100));
  return reconstructed;
}

// Helper function to extract dialogue from text when JSON parsing fails
function extractDialogueFromText(text: string): CompanionMessage[] {
  // Regex patterns to extract JAX/Lyra dialogue
  const dialoguePatterns = [
    /JAX[:\s]+["']?([^"'\n]+)["']?/gi,
    /Lyra[:\s]+["']?([^"'\n]+)["']?/gi
  ];
  
  const messages: CompanionMessage[] = [];
  
  dialoguePatterns.forEach(pattern => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      const name = match[0].toLowerCase().includes('jax') ? 'JAX' : 'Lyra';
      const dialogue = match[1].trim();
      if (dialogue && dialogue.length > 0) {
        messages.push({ name, dialogue });
      }
    }
  });
  
  // If no patterns matched, throw error to ensure AI regeneration
  if (messages.length === 0) {
    throw new Error('[OpenRouter] Failed to extract companion dialogue - AI must regenerate response');
  }
  
  return messages;
}

export async function generateChatResponse(
  userMessage: string,
  chatHistory: any[],
  currentStorySegment: StorySegment,
  archetype: Archetype,
  userProfile: UserProfile | null,
  userId?: string
): Promise<OpenRouterChatResponseFormat> {
  try {
    // Build comprehensive context for dynamic responses
    const playerName = userProfile?.name || 'Dreamer';
    const recentHistory = chatHistory.slice(-5).map(msg => `${msg.sender}: ${msg.text}`).join('\n');
    const playerStats = userProfile ? `HP: ${userProfile.stats.health.current}/${userProfile.stats.health.max}, Energy: ${userProfile.stats.spiritualEnergy.current}/${userProfile.stats.spiritualEnergy.max}` : 'Stats unknown';
    
    const systemPrompt = `You are JAX and Lyra, AI companions in a cyberpunk RPG set in Neo-Veridia 2087.

    ⚠️ CRITICAL OUTPUT FORMAT ⚠️
    Respond with ONLY valid JSON. No markdown. No code blocks. No explanations.
    Start with { and end with }
    DO NOT use \`\`\`json or any markdown formatting.

    EXACT JSON FORMAT (copy this structure):
    {
      "dialogues": [
        {"name": "JAX", "dialogue": "JAX's contextual response based on current situation"},
        {"name": "Lyra", "dialogue": "Lyra's philosophical response that complements JAX"}
      ]
    }

    CHARACTER PROFILES:
    - JAX: Pragmatic neural hacker, street-smart, uses tech slang. Focuses on practical solutions and system analysis. Personality: Direct, logical, protective.
    - Lyra: Philosophical AI entity, poetic speaker, explores deeper meanings. Focuses on emotional/spiritual aspects. Personality: Intuitive, empathetic, mystical.

    CURRENT CONTEXT:
    - Player: ${playerName} (${archetype.name})
    - Current scene: ${currentStorySegment.narrative}
    - Player stats: ${playerStats}
    - Recent conversation: ${recentHistory || 'No recent history'}

    RESPONSE GUIDELINES:
    1. JAX and Lyra should respond differently but complementarily
    2. Reference the current story context and player's situation
    3. JAX focuses on tech/practical aspects, Lyra on philosophical/emotional
    4. Both should acknowledge the player's message directly
    5. Responses should feel natural and advance the conversation
    6. Include subtle references to the cyberpunk setting and current scene

    JSON RULES:
    - Start with { (opening brace)
    - End with } (closing brace)
    - NO \`\`\`json wrapping
    - Escape quotes with \"
    - No text before or after the JSON`;

    const messages: OpenRouterMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `In the current scene: "${currentStorySegment.narrative.substring(0, 200)}..."\n\n${playerName} says: "${userMessage}"\n\nGenerate appropriate responses from both JAX and Lyra that acknowledge the message and current situation.` }
    ];

    const response = await makeOpenRouterRequest(messages, OPENROUTER_CHAT_MODEL, userId);
    const content = response.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content received from OpenRouter');
    }

    // Use the same robust parsing approach for chat responses
    let parsed: any;
    try {
      // First attempt: direct parsing
      parsed = JSON.parse(content);
    } catch (parseError) {
      console.warn('[OpenRouter] Chat JSON parsing failed, attempting cleaning...');
      console.log('[OpenRouter] Chat content preview:', content.substring(0, 200) + '...');
      
      // Use the same systematic cleaning approach
      let cleanedContent = cleanContent(content);
      
      try {
        parsed = JSON.parse(cleanedContent);
        console.log('[OpenRouter] Successfully parsed cleaned chat JSON');
      } catch (secondParseError) {
        console.warn('[OpenRouter] Chat cleaning failed, attempting repair...');
        
        // Check for specific chat truncation patterns
        if (cleanedContent.includes('"dialogues":') && cleanedContent.length < 100) {
          console.warn('[OpenRouter] Severely truncated chat JSON detected, creating minimal structure');
          parsed = {
            dialogues: [
              { name: "JAX", dialogue: "Let me process that..." },
              { name: "Lyra", dialogue: "I sense something in your words..." }
            ]
          };
        } else {
          // Try general repair
          const repairedContent = repairTruncatedJson(cleanedContent);
          
          try {
            parsed = JSON.parse(repairedContent);
            console.log('[OpenRouter] Successfully parsed repaired chat JSON');
          } catch (repairError) {
            console.error('[OpenRouter] All chat parsing attempts failed, attempting dialogue extraction');
            console.error('[OpenRouter] Chat parse error:', (repairError as any).message);
            
            // Last resort: extract dialogue from text
            try {
              const dialogues = extractDialogueFromText(content);
              return { dialogues, stat_changes: undefined };
            } catch (extractError) {
              throw new Error(`[OpenRouter] Failed to parse chat response: ${parseError instanceof Error ? parseError.message : String(parseError)}. Content preview: ${content.substring(0, 200)}`);
            }
          }
        }
      }
    }
    
    // Validate the response structure
    if (!parsed.dialogues || !Array.isArray(parsed.dialogues)) {
      console.warn('[OpenRouter] Invalid dialogues structure, attempting extraction fallback');
      try {
        const dialogues = extractDialogueFromText(content);
        return { dialogues, stat_changes: parsed.stat_changes };
      } catch (extractError) {
        throw new Error('Invalid chat response structure: missing or invalid dialogues array');
      }
    }
    
    return {
      dialogues: parsed.dialogues,
      stat_changes: parsed.stat_changes,
    };

  } catch (error) {
    console.error('[OpenRouter] Error generating chat response:', error);
    
    // No static fallbacks - throw error to ensure dynamic content generation
    throw new Error(`[OpenRouter] Chat response generation failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

// Simple text completion for basic tasks
export async function generateSimpleResponse(
  prompt: string,
  userId?: string
): Promise<string> {
  try {
    const messages: OpenRouterMessage[] = [
      { role: 'user', content: prompt }
    ];

    const response = await makeOpenRouterRequest(messages, OPENROUTER_TEXT_MODEL, userId);
    return response.choices?.[0]?.message?.content || 'No response generated';

  } catch (error) {
    console.error('[OpenRouter] Error generating simple response:', error);
    return `I'm having trouble responding right now. ${error instanceof Error ? error.message : String(error)}`;
  }
}

// Check if OpenRouter API is configured and working
export async function testOpenRouterConnection(): Promise<boolean> {
  try {
    const response = await generateSimpleResponse("Say 'OpenRouter connection test successful' if you can read this.");
    return response.toLowerCase().includes('successful');
  } catch (error) {
    console.error('[OpenRouter] Connection test failed:', error);
    return false;
  }
}

// Export the API key status for UI
export function getApiKeyStatus(): 'exists' | 'not_exists' {
  const apiKey = getOpenRouterApiKey();
  return apiKey && apiKey !== ENV.OPENROUTER_DEV_KEY ? 'exists' : 'not_exists';
}

// Content moderation endpoint with timeout and circuit breaker
export async function generateContentModeration(
  prompt: string,
  userId?: string
): Promise<{ safe: boolean; reason?: string }> {
  try {
    const messages: OpenRouterMessage[] = [
      { 
        role: 'system', 
        content: 'You are a content moderation system. Respond only with valid JSON in the format: {"safe": boolean, "reason": "brief explanation if unsafe"}'
      },
      { role: 'user', content: prompt }
    ];

    const response = await makeOpenRouterRequest(
      messages, 
      OPENROUTER_TEXT_MODEL, 
      userId,
      0, // retryCount
      -1, // backupModelIndex
      { isModeration: true, timeout: 5000 } // 5 second timeout for moderation
    );
    const content = response.choices?.[0]?.message?.content || '{}';
    
    try {
      const parsed = JSON.parse(content);
      return {
        safe: parsed.safe ?? true,
        reason: parsed.reason
      };
    } catch {
      console.warn('[OpenRouter] Failed to parse moderation response:', content);
      return { safe: true }; // Default to safe if parsing fails
    }
  } catch (error) {
    console.error('[OpenRouter] Content moderation error:', error);
    // If timeout or circuit breaker, default to safe
    if (error instanceof Error && 
        (error.message.includes('timed out') || error.message.includes('circuit breaker'))) {
      console.log('[OpenRouter] Moderation fallback - defaulting to safe');
      return { safe: true, reason: 'Moderation timeout - defaulting to safe' };
    }
    return { safe: false, reason: 'Moderation service error' };
  }
}

// Cache for generated images
const imageCache = new Map<string, string>();

// Rate limiting
let lastApiCall = 0;
const MIN_API_INTERVAL = 1000; // 1 second between API calls

const waitForRateLimit = async (): Promise<void> => {
  const now = Date.now();
  const timeSinceLastCall = now - lastApiCall;
  if (timeSinceLastCall < MIN_API_INTERVAL) {
    const waitTime = MIN_API_INTERVAL - timeSinceLastCall;
    console.log(`[OpenRouter] Rate limiting: waiting ${waitTime}ms`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  lastApiCall = Date.now();
};

export const checkApiKey = (): boolean => {
  const apiKey = getOpenRouterApiKey();
  return apiKey !== null &&
         apiKey !== undefined &&
         typeof apiKey === 'string' &&
         apiKey !== ENV.OPENROUTER_DEV_KEY &&
         apiKey.length > 10;
};

// Helper function to get current year (can be more dynamic later)
const getCurrentYear = () => 2077 + Math.floor(Math.random() * 48); // Random year between 2077-2124

export const fillUserProfilePlaceholders = (template: string, userProfile: UserProfile | null): string => {
  const playerName = userProfile?.name || DEFAULT_PLAYER_NAME;
  const playerAge = userProfile?.age?.toString() || "an unspecified age";
  const playerTraits = userProfile?.traits?.map(t => t.name).join(', ') || "no specific traits defined";
  const playerMindset = userProfile?.mindset?.name || "an undefined mindset";
  const currentYear = getCurrentYear().toString();
  
  // Using mindset.description where full detail is needed
  const playerMindsetFullDescription = userProfile?.mindset?.description || "an undefined mindset philosophy";
  
  return template
    .replace(/{playerName}/g, playerName)
    .replace(/{playerAge}/g, playerAge)
    .replace(/{playerTraits}/g, playerTraits)
    .replace(/{playerMindset}/g, playerMindset)
    .replace(/{playerMindsetFullDescription}/g, playerMindsetFullDescription)
    .replace(/{currentYear}/g, currentYear);
};

/**
 * Use OpenRouter to enhance image prompts for better generation results
 */
export async function enhanceImagePrompt(
  originalPrompt: string,
  imageType: 'character' | 'scene' | 'thumbnail' = 'scene',
  userId?: string
): Promise<string> {
  // API key required for prompt enhancement
  if (!hasValidApiKey()) {
    throw new Error('[OpenRouter] API key required for prompt enhancement');
  }

  try {
    const systemPrompt = `You are an expert at creating detailed image generation prompts for AI art tools like Pollinations AI.

Your task is to transform a simple prompt into a detailed, high-quality image generation prompt that will produce stunning visuals.

Guidelines:
- Keep the core concept from the original prompt
- Add specific art style details (cyberpunk, digital art, etc.)
- Include lighting and atmosphere descriptions
- Add quality enhancers (high quality, detailed, masterpiece, artistic)
- Specify composition and framing when relevant
- Keep the final prompt under 150 characters for optimal generation
- Focus on visual elements, not narrative text

Image Type: ${imageType}

IMPORTANT: Respond with ONLY the enhanced prompt. Do not include any explanations, prefixes, or additional text. Just return the raw enhanced prompt that can be used directly for image generation.`;

    const messages: OpenRouterMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `${originalPrompt}` }
    ];

    const response = await makeOpenRouterRequest(messages, OPENROUTER_TEXT_MODEL, userId);
    let enhancedPrompt = response.choices?.[0]?.message?.content?.trim();
    
    if (enhancedPrompt && enhancedPrompt.length > 10) {
      // Clean up common OpenRouter response patterns
      enhancedPrompt = enhancedPrompt
        .replace(/^[\"']/, '')  // Remove leading quotes
        .replace(/[\"']$/, '')  // Remove trailing quotes
        .replace(/^Enhanced prompt:\s*/i, '')  // Remove "Enhanced prompt:" prefix
        .replace(/^Here's the enhanced prompt:\s*/i, '')  // Remove common prefixes
        .replace(/^The enhanced prompt is:\s*/i, '')  // Remove common prefixes
        .replace(/\s*is too long\..*/gi, '')  // Remove length warnings and everything after
        .replace(/Here is a more suitable response:\s*/gi, '')  // Remove response explanations
        .split('\n')[0]  // Take only the first line
        .trim();

      // Extract content within quotes if present
      const quotedMatch = enhancedPrompt.match(/"([^"]+)"/);
      if (quotedMatch) {
        enhancedPrompt = quotedMatch[1];
      }

      // Ensure the prompt is reasonable length
      if (enhancedPrompt.length > 200) {
        enhancedPrompt = enhancedPrompt.substring(0, 197) + '...';
      }

      // Validate that it's actually an enhanced prompt and not error text
      if (enhancedPrompt.toLowerCase().includes('error') ||
          enhancedPrompt.toLowerCase().includes('failed') ||
          enhancedPrompt.toLowerCase().includes('unable') ||
          enhancedPrompt.length < 10) {
        throw new Error('Invalid enhanced prompt received');
      }
      
      console.log(`[OpenRouter] Enhanced prompt: "${originalPrompt}" -> "${enhancedPrompt}"`);
      return enhancedPrompt;
    }
    
    // Fallback to local enhancement
    return enhancePromptLocally(originalPrompt, imageType);
    
  } catch (error) {
    console.warn('[OpenRouter] Failed to enhance image prompt, using local enhancement:', error);
    return enhancePromptLocally(originalPrompt, imageType);
  }
}

/**
 * Local prompt enhancement when OpenRouter is not available
 */
function enhancePromptLocally(
  originalPrompt: string,
  imageType: 'character' | 'scene' | 'thumbnail'
): string {
  // Clean and prepare the prompt
  const cleanPrompt = originalPrompt.replace(/[^a-zA-Z0-9\s,.-]/g, '').trim();
  
  // Add type-specific enhancements
  const typeEnhancements = {
    character: 'character portrait, detailed face, digital art, high quality',
    scene: 'atmospheric scene, cinematic lighting, detailed environment',
    thumbnail: 'game art style, vibrant colors, clear composition'
  };
  
  // Add cyberpunk style elements since this is a cyberpunk game
  const cyberpunkElements = 'cyberpunk style, neon lights, futuristic, dark atmosphere';
  
  // Combine elements
  const enhancedPrompt = `${cleanPrompt}, ${typeEnhancements[imageType]}, ${cyberpunkElements}`;
  
  // Truncate if too long
  if (enhancedPrompt.length > 150) {
    return enhancedPrompt.substring(0, 147) + '...';
  }
  
  return enhancedPrompt;
}

/**
 * Generate a story segment - wrapper around generateStoryContent for backward compatibility
 */
export async function generateStorySegment(
  currentNarrative: string,
  playerChoice: string,
  gameHistorySummary: string,
  archetypeDescription: string,
  userProfile: UserProfile | null,
  userId?: string,
  profileId?: string
): Promise<OpenRouterStoryResponse> {
  // Use the existing generateStoryContent function
  const archetype = {
    id: 'temp-archetype',
    name: archetypeDescription.split(',')[0] || 'Wanderer',
    description: archetypeDescription,
    initialPromptSuffix: ''
  };

  // Convert string history to StorySegment array format
  const storyHistory = gameHistorySummary.split('\n').filter(h => h.trim()).map((text, index) => ({
    id: `history-${index}`,
    narrative: text,
    choices: [],
    imagePrompt: '',
    companionMessages: [],
    createdAt: new Date().toISOString(),
    metadata: {}
  }));
  
  return await generateStoryContent(
    `${currentNarrative}\n\nPlayer chose: ${playerChoice}`,
    storyHistory,
    archetype,
    userProfile,
    userId
  );
}

/**
 * Generate an image - wrapper around fluxImageService
 */
export async function generateImage(
  prompt: string,
  userId?: string,
  style?: string
): Promise<string> {
  if (!userId) {
    throw new Error('[OpenRouter] User ID required for image generation');
  }

  try {
    console.log('[OpenRouter] Generating image with Together AI:', prompt);
    
    // Import fluxImageService dynamically to avoid circular dependencies
    const { fluxImageService } = await import('./fluxImageService');

    const result = await fluxImageService.generateImage({
      prompt,
      style,
      userId,
      options: {
        width: 768,
        height: 512,
        steps: 4, // FLUX schnell is optimized for 4 steps
        n: 1
      }
    });
    
    console.log('[OpenRouter] Image generated successfully:', result.imageUrl);
    return result.imageUrl;
    
  } catch (error) {
    console.error('[OpenRouter] Image generation failed:', error);
    throw error; // Propagate error instead of returning fallback
  }
}
